import {
	CheckOutlined,
	CloseOutlined,
	DeleteOutlined,
	EditOutlined,
	GlobalOutlined,
	PlusOutlined,
	TranslationOutlined,
} from '@ant-design/icons'
import {
	Badge,
	Button,
	Form,
	Input,
	Modal,
	Popconfirm,
	Space,
	Switch,
	Table,
	Tag,
	Tooltip,
	message,
} from 'antd'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import api from '../../api/index.api'
import { ROUTE } from '../../utils/constant'

const RTL_PREVIEW_STYLE = { direction: 'rtl', textAlign: 'right' }

const BASELINE_CODES = new Set(['en', 'vi']) // cannot be deleted

export const I18nLanguageList = () => {
	const history = useHistory()
	const [items, setItems] = useState([])
	const [loading, setLoading] = useState(false)
	const [modalOpen, setModalOpen] = useState(false)
	const [editingCode, setEditingCode] = useState(null) // null = create, string = edit
	const [saving, setSaving] = useState(false)
	const [form] = Form.useForm()
	const mountedRef = useRef(true)

	useEffect(() => {
		mountedRef.current = true
		return () => { mountedRef.current = false }
	}, [])

	const load = useCallback(async () => {
		setLoading(true)
		try {
			const rs = await api.i18nLanguageApi.list()
			if (mountedRef.current) {
				setItems(Array.isArray(rs?.data) ? rs.data : [])
			}
		} catch (err) {
			message.error(err?.message || 'Could not load languages.')
		} finally {
			if (mountedRef.current) setLoading(false)
		}
	}, [])

	useEffect(() => { load() }, [load])

	const openCreate = () => {
		setEditingCode(null)
		form.resetFields()
		form.setFieldsValue({ isActive: true, isRtl: false, sortOrder: items.length })
		setModalOpen(true)
	}

	const openEdit = (record) => {
		setEditingCode(record.code)
		form.setFieldsValue({
			name: record.name,
			nativeName: record.nativeName,
			isRtl: record.isRtl,
			isActive: record.isActive,
			sortOrder: record.sortOrder,
		})
		setModalOpen(true)
	}

	const handleSave = async () => {
		let values
		try {
			values = await form.validateFields()
		} catch {
			return
		}
		setSaving(true)
		try {
			if (editingCode) {
				await api.i18nLanguageApi.update(editingCode, {
					name: values.name,
					nativeName: values.nativeName,
					isRtl: values.isRtl,
					isActive: values.isActive,
					sortOrder: Number(values.sortOrder) || 0,
				})
				message.success('Language updated.')
			} else {
				await api.i18nLanguageApi.create({
					code: values.code.trim().toLowerCase(),
					name: values.name,
					nativeName: values.nativeName,
					isRtl: values.isRtl ?? false,
					isActive: values.isActive ?? true,
					sortOrder: Number(values.sortOrder) || 0,
				})
				message.success('Language created.')
			}
			setModalOpen(false)
			load()
		} catch (err) {
			message.error(err?.message || 'Save failed.')
		} finally {
			setSaving(false)
		}
	}

	const handleToggleActive = async (record) => {
		try {
			await api.i18nLanguageApi.update(record.code, { isActive: !record.isActive })
			message.success(`Language ${!record.isActive ? 'activated' : 'deactivated'}.`)
			load()
		} catch (err) {
			message.error(err?.message || 'Update failed.')
		}
	}

	const handleDelete = async (code) => {
		try {
			await api.i18nLanguageApi.remove(code)
			message.success('Language deleted.')
			load()
		} catch (err) {
			message.error(err?.message || 'Delete failed.')
		}
	}

	const goToTranslations = (code) => {
		history.push(`${ROUTE.I18N_MANAGEMENT}${ROUTE.I18N_TRANSLATIONS}/${code}`)
	}

	const columns = [
		{
			title: 'Code',
			dataIndex: 'code',
			width: 80,
			render: (code) => <Tag color="blue">{code}</Tag>,
		},
		{
			title: 'Name (EN)',
			dataIndex: 'name',
			width: 180,
		},
		{
			title: 'Native name',
			dataIndex: 'nativeName',
			width: 180,
			render: (val, record) => (
				<span style={record.isRtl ? RTL_PREVIEW_STYLE : undefined}>{val}</span>
			),
		},
		{
			title: 'RTL',
			dataIndex: 'isRtl',
			width: 60,
			align: 'center',
			render: (v) =>
				v ? <CheckOutlined style={{ color: '#52c41a' }} /> : <CloseOutlined style={{ color: '#ccc' }} />,
		},
		{
			title: 'Sort',
			dataIndex: 'sortOrder',
			width: 60,
			align: 'center',
		},
		{
			title: 'Active',
			dataIndex: 'isActive',
			width: 80,
			align: 'center',
			render: (active, record) => (
				<Switch
					checked={active}
					size="small"
					onChange={() => handleToggleActive(record)}
					disabled={BASELINE_CODES.has(record.code)}
				/>
			),
		},
		{
			title: 'UI Strings',
			width: 100,
			align: 'center',
			render: (_, record) => (
				<Badge count={record.translationCount ?? 0} overflowCount={9999} size="small">
					<Button
						size="small"
						icon={<TranslationOutlined />}
						onClick={() => goToTranslations(record.code)}
					>
						Edit
					</Button>
				</Badge>
			),
		},
		{
			title: 'Actions',
			width: 100,
			align: 'center',
			render: (_, record) => (
				<Space>
					<Tooltip title="Edit">
						<Button
							size="small"
							icon={<EditOutlined />}
							onClick={() => openEdit(record)}
						/>
					</Tooltip>
					<Tooltip title={BASELINE_CODES.has(record.code) ? 'Baseline language cannot be deleted' : 'Delete'}>
						<Popconfirm
							title="Delete this language? All UI overrides will also be removed."
							onConfirm={() => handleDelete(record.code)}
							disabled={BASELINE_CODES.has(record.code)}
						>
							<Button
								size="small"
								danger
								icon={<DeleteOutlined />}
								disabled={BASELINE_CODES.has(record.code)}
							/>
						</Popconfirm>
					</Tooltip>
				</Space>
			),
		},
	]

	return (
		<div style={{ padding: 24 }}>
			<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
				<Space>
					<GlobalOutlined style={{ fontSize: 20 }} />
					<span style={{ fontSize: 18, fontWeight: 600 }}>Languages</span>
				</Space>
				<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
					Add language
				</Button>
			</div>

			<Table
				rowKey="code"
				columns={columns}
				dataSource={items}
				loading={loading}
				pagination={false}
				size="small"
				bordered
			/>

			<Modal
				title={editingCode ? `Edit language: ${editingCode}` : 'Add new language'}
				open={modalOpen}
				onCancel={() => setModalOpen(false)}
				onOk={handleSave}
				okText="Save"
				confirmLoading={saving}
				destroyOnClose
			>
				<Form form={form} layout="vertical">
					{!editingCode && (
						<Form.Item
							name="code"
							label="Language code (BCP-47, e.g. es, id, ar, pt-BR)"
							rules={[
								{ required: true, message: 'Code is required' },
								{
									pattern: /^[a-z]{2,3}(-[a-z0-9]{2,8})?$/,
									message: 'Use lowercase BCP-47 format (e.g. es, id, pt-BR)',
								},
							]}
						>
							<Input placeholder="es" maxLength={10} />
						</Form.Item>
					)}
					<Form.Item
						name="name"
						label="Name in English"
						rules={[{ required: true, message: 'Name is required' }]}
					>
						<Input placeholder="Spanish" />
					</Form.Item>
					<Form.Item
						name="nativeName"
						label="Native name"
						rules={[{ required: true, message: 'Native name is required' }]}
					>
						<Input placeholder="Español" />
					</Form.Item>
					<Form.Item name="sortOrder" label="Sort order (lower = shown first)">
						<Input type="number" min={0} />
					</Form.Item>
					<Space size="large">
						<Form.Item name="isRtl" label="Right-to-left (RTL)" valuePropName="checked">
							<Switch />
						</Form.Item>
						<Form.Item name="isActive" label="Active" valuePropName="checked">
							<Switch />
						</Form.Item>
					</Space>
				</Form>
			</Modal>
		</div>
	)
}
